/** Typed-ish fetch client. Token lives in localStorage; 401 clears it. */

const TOKEN_KEY = 'solfo.token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T = unknown>(
  path: string,
  opts: { method?: string; body?: unknown; formData?: FormData } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(path.startsWith('/') ? path : `/api/${path}`, {
    method: opts.method ?? (opts.body !== undefined || opts.formData ? 'POST' : 'GET'),
    headers,
    body: opts.formData ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
  });
  if (res.status === 401 && getToken()) {
    setToken(null);
    window.location.href = '/login';
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* not json */
    }
    throw new ApiError(res.status, message);
  }
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

/** Authenticated media URL helper (files stream through the API with authz). */
export async function fetchFileUrl(fileId: string): Promise<string> {
  const res = await fetch(`/api/files/${fileId}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new ApiError(res.status, 'Could not load file');
  return URL.createObjectURL(await res.blob());
}

export async function uploadFile(blob: Blob, filename: string): Promise<{ id: string; mime: string }> {
  const fd = new FormData();
  fd.append('file', blob, filename);
  return api('/api/files', { formData: fd });
}
